// Edit dialog for an existing property. Mirrors the field set of
// CreatePropertyDialog minus the seller picker (reassignment is the
// Transfer flow, gated by `ownership_locked`). Submits a PATCH to
// /properties/:id via the existing useUpdateProperty hook.
//
// Price is stored as whole EUR units in D1. The input accepts DE
// format (390.000 with thousand separators) and parseDeNumber()
// normalizes before save.
//
// Every input read back through parseDeNumber must be seeded by its
// formatter, never String(n): the areas were, so 50.37 m² went in as
// "50.37" and came back as 5037 on every save (Spengergasse 21,
// 2026-08-15 and 08-18).

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  type CreatePropertyPayload,
  type PropertyRow,
  useUpdateProperty,
} from '@/hooks/data/useProperties';
import { BUILDING_TYPES, OBJECT_TYPES, VIENNA_DISTRICTS } from '@/lib/austria';
import { parseDeNumber, formatDeDecimal, formatDeInteger } from '@/lib/parseDeNumber';

interface Props {
  property: PropertyRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EDI_REGEX = /^2483\/\d{5}$/;

const STAGE_OPTIONS: PropertyRow['marketing_stage'][] = [
  'lead',
  'aufnahme',
  'active',
  'pending',
];

type EditPayload = Omit<CreatePropertyPayload, 'clientId'>;

function fromRow(p: PropertyRow): EditPayload {
  return {
    city: p.city ?? 'Wien',
    address: p.address ?? null,
    postalCode: p.postal_code ?? null,
    street: p.street ?? null,
    streetNumber: p.street_number ?? null,
    entrance: p.entrance ?? null,
    floor: p.floor ?? null,
    doorNumber: p.door_number ?? null,
    district: p.district ?? null,
    buildingType: p.building_type ?? null,
    objectType: p.object_type ?? null,
    roomsCount: p.rooms_count ?? null,
    propertySize: p.property_size ?? null,
    propertyArea: p.property_area ?? null,
    purchasePrice: p.purchase_price ?? null,
    ediRealNumber: p.edi_real_number ?? null,
    kgGrundbuch: p.kg_grundbuch ?? null,
    einlagezahl: p.einlagezahl ?? null,
    bLaufnummer: p.b_laufnummer ?? null,
    marketingStage: p.marketing_stage,
  };
}

export function EditPropertyDialog({ property, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const update = useUpdateProperty(property.id);

  const [form, setForm] = useState<EditPayload>(() => fromRow(property));
  // Areas displayed with a decimal comma (e.g. "50,37").
  const [size, setSize] = useState<string>(formatDeDecimal(property.property_size));
  const [area, setArea] = useState<string>(formatDeDecimal(property.property_area));
  // Price displayed in DE format with thousand separators (e.g. "390.000").
  const [price, setPrice] = useState<string>(formatDeInteger(property.purchase_price));
  const [err, setErr] = useState<string | null>(null);

  // Re-seed when the dialog reopens against a different property (the
  // ViewPropertyDialog can chain into Edit on a different row).
  useEffect(() => {
    if (open) {
      setForm(fromRow(property));
      setSize(formatDeDecimal(property.property_size));
      setArea(formatDeDecimal(property.property_area));
      setPrice(formatDeInteger(property.purchase_price));
      setErr(null);
    }
  }, [open, property]);

  const set = <K extends keyof EditPayload>(k: K, v: EditPayload[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const stageLabels = useMemo(
    () =>
      ({
        lead: t('enums.marketing_stage.lead', 'Lead'),
        aufnahme: t('enums.marketing_stage.aufnahme', 'Aufnahme'),
        active: t('enums.marketing_stage.active', 'Aktiv'),
        pending: t('enums.marketing_stage.pending', 'Pending'),
      }) as Record<string, string>,
    [t],
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);

    if (form.ediRealNumber && !EDI_REGEX.test(form.ediRealNumber)) {
      setErr('EDI Real-Nummer muss dem Format 2483/XXXXX entsprechen.');
      return;
    }

    const payload: Partial<CreatePropertyPayload> = {
      city: (form.city ?? '').trim() || 'Wien',
      district: form.district || null,
      street: form.street ?? null,
      streetNumber: form.streetNumber ?? null,
      postalCode: form.postalCode ?? null,
      entrance: form.entrance ?? null,
      floor: form.floor ?? null,
      doorNumber: form.doorNumber ?? null,
      buildingType: form.buildingType ?? null,
      objectType: form.objectType ?? null,
      roomsCount: form.roomsCount ?? null,
      propertySize: parseDeNumber(size),
      propertyArea: parseDeNumber(area),
      purchasePrice: (() => {
        const n = parseDeNumber(price);
        return n != null && n > 0 ? Math.round(n) : null;
      })(),
      ediRealNumber: form.ediRealNumber ?? null,
      kgGrundbuch: form.kgGrundbuch ?? null,
      einlagezahl: form.einlagezahl ?? null,
      bLaufnummer: form.bLaufnummer ?? null,
      marketingStage: form.marketingStage,
    };

    try {
      await update.mutateAsync(payload);
      onOpenChange(false);
    } catch (cause) {
      setErr((cause as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('properties.edit.title', 'Immobilie bearbeiten')}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 col-span-2">
              <Label>{t('properties.fields.street', 'Straße')}</Label>
              <Input
                value={form.street ?? ''}
                onChange={(e) => set('street', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.streetNumber', 'Hausnummer')}</Label>
              <Input
                value={form.streetNumber ?? ''}
                onChange={(e) => set('streetNumber', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.postalCode', 'PLZ')}</Label>
              <Input
                value={form.postalCode ?? ''}
                onChange={(e) => set('postalCode', e.target.value)}
                maxLength={4}
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>{t('properties.fields.city', 'Stadt')} *</Label>
              <Input
                value={form.city ?? ''}
                onChange={(e) => set('city', e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>{t('properties.fields.district', 'Bezirk')}</Label>
              <Select
                value={form.district ?? ''}
                onChange={(e) => set('district', e.target.value)}
              >
                <option value="">— auswählen —</option>
                {VIENNA_DISTRICTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>{t('properties.fields.entrance', 'Stiege')}</Label>
              <Input
                value={form.entrance ?? ''}
                onChange={(e) => set('entrance', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.floor', 'Stock')}</Label>
              <Input
                value={form.floor ?? ''}
                onChange={(e) => set('floor', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.doorNumber', 'Tür')}</Label>
              <Input
                value={form.doorNumber ?? ''}
                onChange={(e) => set('doorNumber', e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>{t('properties.fields.objectType', 'Objekttyp')}</Label>
              <Select
                value={form.objectType ?? ''}
                onChange={(e) => set('objectType', e.target.value)}
              >
                <option value="">— auswählen —</option>
                {OBJECT_TYPES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.buildingType', 'Gebäudetyp')}</Label>
              <Select
                value={form.buildingType ?? ''}
                onChange={(e) => set('buildingType', e.target.value)}
              >
                <option value="">— auswählen —</option>
                {BUILDING_TYPES.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.roomsCount', 'Zimmer')}</Label>
              <Input
                value={form.roomsCount ?? ''}
                onChange={(e) => set('roomsCount', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.size', 'Wohnfläche (m²)')}</Label>
              <Input
                inputMode="decimal"
                value={size}
                onChange={(e) => setSize(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.area', 'Grundfläche (m²)')}</Label>
              <Input
                inputMode="decimal"
                value={area}
                onChange={(e) => setArea(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.price', 'Kaufpreis (€)')}</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="450.000"
              />
            </div>

            <div className="space-y-1.5 col-span-2">
              <Label>{t('properties.fields.marketingStage', 'Vermarktungs-Phase')}</Label>
              <Select
                value={form.marketingStage ?? 'lead'}
                onChange={(e) =>
                  set('marketingStage', e.target.value as PropertyRow['marketing_stage'])
                }
              >
                {STAGE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {stageLabels[s] ?? s}
                  </option>
                ))}
              </Select>
              <p className="text-[11px] text-ink-3">
                {t(
                  'properties.edit.stage_hint',
                  'Stufenwechsel jenseits der vier Standard-Phasen erfolgt automatisch über den Anbots-/Notar-Workflow.',
                )}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>EDI Real-Nummer</Label>
              <Input
                value={form.ediRealNumber ?? ''}
                onChange={(e) => set('ediRealNumber', e.target.value)}
                placeholder="2483/00000"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.kgGrundbuch', 'KG / Grundbuch')}</Label>
              <Input
                value={form.kgGrundbuch ?? ''}
                onChange={(e) => set('kgGrundbuch', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.einlagezahl', 'Einlagezahl')}</Label>
              <Input
                value={form.einlagezahl ?? ''}
                onChange={(e) => set('einlagezahl', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('properties.fields.bLaufnummer', 'B-Laufnummer')}</Label>
              <Input
                value={form.bLaufnummer ?? ''}
                onChange={(e) => set('bLaufnummer', e.target.value)}
              />
            </div>
          </div>

          {err && (
            <p className="rounded-md border border-[hsl(var(--negative))]/30 bg-[hsl(var(--negative))]/5 px-3 py-2 text-xs text-[hsl(var(--negative))]">
              {err}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={update.isPending}
            >
              {t('common.cancel', 'Abbrechen')}
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending
                ? t('common.saving', 'Speichern …')
                : t('common.save', 'Speichern')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
